import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { Search, User, ShoppingBag, Menu, Heart } from 'lucide-react';
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from '@/components/ui/sheet';

const Wordmark = ({ className = '' }) => (
  <Link to="/" data-testid="header-logo" aria-label="NICEWINE — home" className={`block text-center leading-none ${className}`}>
    <div className="font-serif text-nw-gold text-[26px] sm:text-[30px] tracking-[0.28em] pl-[0.28em]">NICEWINE</div>
    <div className="text-nw-warmgrey text-[8px] sm:text-[9px] tracking-luxe uppercase mt-1">Import &amp; Export · Vins &amp; Spiritueux</div>
  </Link>
);

export const Header = () => {
  const { t, cartCount, customer } = useStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');

  const links = [
    { label: t.nav.home, to: '/', id: 'home' },
    { label: t.nav.shop, to: '/collections', id: 'shop' },
    { label: t.nav.wines, to: '/collections?category=wine', id: 'wines' },
    { label: t.nav.spirits, to: '/collections?category=spirit', id: 'spirits' },
    { label: t.nav.collections, to: '/collections?rarity=collector', id: 'collections' },
    { label: t.nav.importExport, to: '/import-export', id: 'import-export' },
    { label: t.nav.about, to: '/about', id: 'about' },
  ];

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/collections?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(''); }
  };

  // The account icon always leads to the customer area (sign-in page when signed out).
  const accountTo = customer ? '/account' : '/account/login';

  return (
    <header className="sticky top-0 z-50 bg-nw-black/95 backdrop-blur-sm border-b border-nw-gold/15">
      <div className="nw-container">
        <div className="h-[76px] grid grid-cols-3 items-center">
          <div className="flex items-center gap-4 justify-start">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="header-mobile-menu-button" aria-label="Menu" className="lg:hidden text-nw-ivory hover:text-nw-gold p-2 -ml-2">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-nw-black border-nw-gold/20 text-nw-ivory w-[85vw] max-w-sm">
                <SheetHeader><SheetTitle className="font-serif text-nw-gold text-xl tracking-[0.2em] text-left">NICEWINE</SheetTitle></SheetHeader>
                <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
                  {links.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`}
                      className="py-3 border-b border-nw-gold/10 text-[13px] tracking-luxe uppercase nw-link">{l.label}</Link>
                  ))}
                  <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-contact" className="py-3 border-b border-nw-gold/10 text-[13px] tracking-luxe uppercase nw-link">{t.nav.contact}</Link>
                  <Link to={accountTo} onClick={() => setOpen(false)} data-testid="mobile-nav-account" className="py-3 text-[13px] tracking-luxe uppercase nw-link flex items-center gap-2"><User size={14} /> {customer ? t.account.title : t.account.signIn}</Link>
                  {customer && <Link to="/account?tab=cellar" onClick={() => setOpen(false)} className="py-3 text-[13px] tracking-luxe uppercase nw-link flex items-center gap-2"><Heart size={14} /> {t.account.tabs.cellar}</Link>}
                </nav>
              </SheetContent>
            </Sheet>
            <button data-testid="header-search-button" aria-label={t.nav.search} onClick={() => setSearchOpen((v) => !v)} className="text-nw-ivory hover:text-nw-gold transition-colors p-2 -ml-2">
              <Search size={19} />
            </button>
          </div>
          <div className="flex justify-center"><Wordmark /></div>
          <div className="flex items-center gap-3 sm:gap-5 justify-end">
            <Link to={accountTo} data-testid="header-account-button" aria-label={t.nav.account}
              className={`relative transition-colors p-2 ${customer ? 'text-nw-gold' : 'text-nw-ivory hover:text-nw-gold'}`}>
              <User size={19} />
              {customer && <span className="absolute top-1.5 right-1 w-1.5 h-1.5 rounded-full bg-nw-gold" aria-hidden="true" />}
            </Link>
            <Link to="/cart" data-testid="header-basket-button" aria-label={t.cart.title} className="relative text-nw-ivory hover:text-nw-gold transition-colors p-2 -mr-2">
              <ShoppingBag size={19} />
              {cartCount > 0 && (
                <span data-testid="header-cart-count" className="absolute top-0 right-0 bg-nw-gold text-nw-black text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>
        <nav className="hidden lg:flex items-center justify-center gap-8 pb-4" aria-label="Main">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.id}`} className="text-[11px] tracking-luxe uppercase nw-link">{l.label}</Link>
          ))}
          <Link to="/contact" data-testid="nav-contact" className="text-[11px] tracking-luxe uppercase nw-link">{t.nav.contact}</Link>
        </nav>
        {searchOpen && (
          <form onSubmit={submitSearch} role="search" className="pb-4 animate-fade-in">
            <input autoFocus data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.nav.search}
              placeholder={t.nav.search + '\u2026'}
              className="w-full bg-transparent border-b border-nw-gold/40 py-2 text-nw-ivory placeholder:text-nw-warmgrey/60 focus:outline-none focus:border-nw-gold text-sm tracking-wide" />
          </form>
        )}
      </div>
    </header>
  );
};
